/**
 * Provides URLs to perform requests
 *
 * @returns Object Set of functions to retrieve URLs
 *
 * @module app/components/URLProvider
 */
import _ from 'lodash';

var keyword = 'betting';
var fragmentsKeyword = 'fragments';

/**
 * Array of the existing virtual sports. All other sports will default to horse-racing/flat
 *
 * @type {Array}
 *
 * @private
 */
var virtualSports = [
    'football',
    'greyhound',
    'motor-racing',
    'speedway',
    'cycling',
];

/**
 * Add PDS Event prefix in case it's missing
 *
 * @param {String} eventId Event ID
 *
 * @returns {String}
 *
 * @private
 */
var pdsEventPrefix = function pdsEventPrefix(eventId) {

    eventId = eventId.replace('OB_EV', '');

    return 'OB_EV' + eventId;
};

/**
 * Add PDS Market prefix in case it's missing
 *
 * @param {String} marketId Market ID
 *
 * @returns {String}
 *
 * @private
 */
var pdsMarketPrefix = function pdsMarketPrefix(marketId) {

    marketId = marketId.replace('OB_MA', '');

    return 'OB_MA' + marketId;
};

/**
 * Get a virtual sport.
 * @params {String} sport Sport name.
 * @returns {String} Virtual sport name.
 * @private
 */
var getVirtualSport = function getVirtualSport(sport) {

    return virtualSports.indexOf(sport) < 0 ? 'horse-racing/flat' : sport;
};

/**
 * Function used to slugify an event or competition
 * name
 *
 * @param  {String} name Event or competition name
 *
 * @return {String}      Slugified name
 *
 * @private
 */
var slugify = function slugify(name) {

    return name.toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/-+/g, '-');
};

/**
 * Checks if given sport belong to racing ones
 *
 * @param {String} sport
 *
 * @returns {Boolean}
 *
 * @private
 */
var isRacing = function isRacing(sport) {

    return (_.indexOf(['horse-racing', 'greyhounds'], sport) > -1);
};

/**
 * Build the partial URL for filtering by sport, or empty string if no filter is applied.
 *
 * @param {String} filterValue The value of the sport filter.
 *
 * @returns {String} The partial URL or empty string
 *
 * @Private
 */
var getScheduleSportFilterPartialUrl = function getScheduleSportFilterPartialUrl(filterValue) {

    return (filterValue) ? '/sport/' + filterValue : '';
};

/**
 * Build the partial URL for filtering by date, or empty string if no filter is applied.
 *
 * @param {String} filterValue The value of the date filter.
 *
 * @returns {String} The partial URL or empty string
 *
 * @Private
 */
var getScheduleDateFilterPartialUrl = function getScheduleDateFilterPartialUrl(filterValue) {

    return (filterValue) ? '/date/' + filterValue : '';
};

/**
 * Build the partial URL for filtering by streaming-only, or empty string
 * if the filter is not applied.
 *
 * @param {String} filterValue The value of the streaming filter.
 *
 * @returns {String} The partial URL or empty string
 *
 * @private
 */
var getScheduleStreamingFilterPartialUrl = function getScheduleStreamingFilterPartialUrl(filterValue) {

    return (filterValue === 'streaming-only') ? '/streaming' : '';
};

/**
 * Build the partial URL for pagination. Defaults to page 0 if no page number is supplied.
 *
 * @param {String} pageNumber The page number
 *
 * @returns {String} The partial URL for the supplied page number.
 *
 * @private
 */
var getSchedulePageNumberPartialUrl = function getSchedulePageNumberPartialUrl(pageNumber) {

    return '/page/' + ((typeof pageNumber !== 'undefined') ? pageNumber : 0);
};

/**
 * Add pagination if not defined
 *
 * @param {Object} params A collection to decorate
 *
 * @returns {void}
 *
 * @private
 */
var addPagination = function addPagination(params) {

    if (_.isUndefined(params.pagination)) {

        params.pagination = 0;
    }
};

/**
 * Composing url
 *
 * @param {string} endpoint Name of endpoint
 * @param {string} fragment Name of fragment
 * @param {Array} params A collection of url parameters
 *
 * @returns {string} Composed url
 *
 * @private
 */
var composeUrl = function composeUrl(endpoint, fragment, params, query) {

    var urlParts = ['', fragment];

    if (endpoint) {

        urlParts.push(endpoint);
    }

    var queryString = _.map(_.toPairs(query), function(entry) {
        return entry[0]+'='+entry[1];
    }).join('&');

    if(queryString){

        queryString = '?'+ queryString;
    }

    return urlParts.concat(params).join('/').replace(/\/$/, '') + queryString;
};

/**
 * Composing url for fragment
 *
 * @param {string} endpoint Name of endpoint
 * @param {Array} params A collection of url parameters
 *
 * @returns {string} Composed url for fragment
 *
 * @private
 */
var composeFragmentUrl = function composeFragmentUrl(endpoint, params, query) {
    return composeUrl(endpoint, fragmentsKeyword, params, query);
};

/**
 * Clearing url parameters from empty strings
 *
 * @param params
 */
var clearUrlParameters = function clearUrlParameters(parameters) {
    return _.reject(parameters, function(parameter){
        return parameter === '';
    });
};

/**
 * Composing url for keyword
 *
 * @param {string} endpoint Name of endpoint
 * @param {Array} params A collection of url parameters
 *
 * @returns {string} Composed url for keyword
 *
 * @private
 */
var composeBaseUrl = function composeBaseUrl(params, query) {

    return composeUrl(null, keyword, params, query);
};

/**
 * Encoding Url if it is not encoded
 *
 * @param {string} url Url to encode
 *
 * @returns {string} Encoded url
 *
 * @private
 */
var encodeUrlIfNotEncoded = function encodeUrlIfNotEncoded(url) {

    var encoded = new RegExp(/%[0-9a-f]{2}/i);

    return encoded.test(url) ? url : encodeURIComponent(url);
};

var api = {

    /**
     * Defines the sport slugs for all sports pages
     *
     * @type {Array}
     *
     * @private
     */
    allSports: ['in-play', 'homepage', 'top-bets', 'apps', 'promotions'],
    sidebarLeft: {
        base: function (args) {

            return composeFragmentUrl('sidebarLeft', [args.locale, args.sport]);
        },
        full: function (args) {

            return composeFragmentUrl('sidebarLeft', [args.locale, args.sport, args.page]);
        },
    },
    competitions: {
        tabFragment: function tabFragment(args) {

            if (args.selectedTab === 'markets') {

                return composeFragmentUrl('collectionsAndMarketGroups', [args.locale, args.sport]);
            } else {

                return api.competitions.regionsByMarketGroup(args);
            }
        },
        regionsByMarketGroup: function regionsByMarketGroup(args) {

            return composeFragmentUrl('regionsByMarketGroupList', [args.locale, args.sport,
                args.selectedTab]) + (args.marketGroup ? '/' + encodeURIComponent(args.marketGroup) : '');
        },
        competitionOutrights: function competitionOutrights(args) {

            addPagination(args);

            return composeFragmentUrl('competitionOutrights', [args.locale, args.sport,
                args.competitionId, args.pagination]);
        },
        competitionEvents: function competitionEvents(args) {

            addPagination(args);
            var params = [args.locale, args.sport, args.competitionId];

            //optional parameter, it's used for outright and market collections, where we are loading events for a
            //competition under a given market group, but not used in the first tab, where we have primary markets
            if (args.marketGroupName) {

                params.push(encodeUrlIfNotEncoded(args.marketGroupName));
            }
            params.push(args.pagination);

            return composeFragmentUrl('competitionEventList', params);
        },
        redirectByFilter: function redirectByFilter(args) {
            return composeBaseUrl([args.locale, args.sport, 'competitions', args.selectedTab]);
        },
    },
    highlights: {
        base: function base(args) {

            return isRacing(args.sport) ? api.meetings.base(args)
                : composeBaseUrl([args.locale, args.sport]);
        },
    },
    matches: {
        base: function base(args) {

            return isRacing(args.sport) ? api.specials.base(args)
                : composeBaseUrl([args.locale, args.sport, 'matches']);
        },
        matches: function matches(args) {

            return composeFragmentUrl('matches', [args.locale, args.sport, args.sortType, args.filter,
                args.marketGroupName]);
        },
        full: function full(args) {

            return composeBaseUrl([args.locale, args.sport, 'matches', args.sortType, args.filter,
                args.marketGroupName]);
        },
    },
    racing: {
        base: args => composeBaseUrl([args.locale, args.sport, 'meetings']),
        racecard: args => composeBaseUrl([
            args.locale,
            args.sport,
            'racecard',
            pdsEventPrefix(args.raceId),
            slugify(args.raceName),
        ]),
        allRaces: args => composeBaseUrl([args.locale, args.sport, 'all-racecards', args.competitionId, args.day]),
    },
    meetings: {
        base: function base(args) {

            return composeBaseUrl([args.locale, args.sport, 'meetings']);
        },
        activePanel: function activePanel(args, localActivePanel) {

            return composeBaseUrl([args.locale, args.sport, 'meetings']) +
                (localActivePanel ? '/' + localActivePanel : '');
        },
        highlights: function highlights(args) {

            return composeFragmentUrl('meetingsHighlights', [args.locale, args.sport]);
        },
        navigation: function navigation(args) {

            return composeFragmentUrl('meetingsNavigation', [args.locale, args.sport]);
        },
        racecard: function racecard(args, request) {

            /**
             * Function used to get the fragment URL
             *
             * @param  {Object} args Args
             *
             * @return {String}      Fragment URL
             *
             * @private
             */
            var getFragmentURL = function (args) {

                return composeFragmentUrl('racecard', [args.locale, args.sport,
                    pdsEventPrefix(args.raceId)]) + (args.collectionId ? '/' + args.collectionId : '');
            };

            return request ? composeBaseUrl([args.locale, args.sport,
                pdsEventPrefix(args.raceId), slugify(args.raceName)]) : getFragmentURL(args);
        },
        meetingMarkets: function meetingMarkets(args, request) {

            return request ? composeBaseUrl([args.locale, args.sport, 'meetings/markets',
                args.meetingMarketsId, slugify(args.meetingMarketsName)]) :
                composeFragmentUrl('meetingMarkets', [args.locale, args.sport, args.meetingMarketsId]);
        },
        getMeetingMarketsByCollection: function getMeetingMarketsByCollection(args) {

            return composeFragmentUrl('eventEntity', [args.locale, args.sport, args.meetingMarketsId,
                args.collectionId]);
        },
        allRaces: function allRaces(args, request) {

            return request ? composeBaseUrl([args.locale, args.sport, 'meetings',
                args.day, args.competitionId, slugify(args.competitionName)]) :
                composeFragmentUrl('allRaces', [args.locale, args.sport, args.day, args.competitionId]);
        },

    },
    antePost: {
        base: function base(args) {

            return composeBaseUrl([args.locale, args.sport, 'future-races']);
        },
        racecard: function racecard(args, request) {

            /**
             * Function used to get the fragment URL
             *
             * @param {Object} args Args
             *
             * @return {String} Fragment URL
             *
             * @private
             */
            var getFragmentURL = function (args) {

                return composeFragmentUrl('racecardAntePost', [args.locale, args.sport,
                    pdsEventPrefix(args.raceId)]) + (args.collectionId ? '/' + args.collectionId : '');
            };

            return request ? composeBaseUrl([args.locale, args.sport,
                pdsEventPrefix(args.raceId), slugify(args.raceName)]) : getFragmentURL(args);
        },
        allRaces: function allRaces(args, request) {

            return request ? composeBaseUrl([args.locale, args.sport, 'future-races', args.competitionId,
                slugify(args.competitionName)]) : composeFragmentUrl('antePostAllRaces',
                [args.locale, args.sport, args.competitionId]);
        },

    },
    specials: {
        base: function base(args) {

            return composeBaseUrl([args.locale, args.sport, 'specials']);
        },
        meetingMarkets: function meetingMarkets(args, request) {

            if (request) {

                return composeBaseUrl([args.locale, args.sport, args.meetingMarketsId,
                    slugify(args.meetingMarketsName)]);
            } else {

                return composeFragmentUrl('specialsMarkets', [args.locale, args.sport,
                    args.meetingMarketsId]);
            }
        },
        getMeetingMarketsByCollection: function getMeetingMarketsByCollection(args) {

            return composeFragmentUrl('eventEntity', [args.locale, args.sport, args.meetingMarketsId,
                args.collectionId]);
        },
    },
    event: {
        getEventPageByCollection: function getEventPageByCollection(args) {

            return composeFragmentUrl('eventEntity', [args.locale, args.sport,
                pdsEventPrefix(args.eventId), args.selectedId]);
        },
        marketCollectionMenu: function marketCollectionMenu(args) {

            return composeFragmentUrl('marketCollections', [args.locale, args.sport,
                pdsEventPrefix(args.eventId), args.selectedId]);
        },
        scoreboard: function scoreboard(args) {

            return composeFragmentUrl('scoreboards', [args.locale, args.sport,
                pdsEventPrefix(args.eventId)]);
        },
        market: function market(args) {

            return composeFragmentUrl('marketEntity', [
                args.locale,
                args.sport,
                pdsEventPrefix(args.eventId),
                pdsMarketPrefix(args.marketId),
            ]);
        },
        base: function base(args) {

            return composeBaseUrl([args.locale, args.sport, pdsEventPrefix(args.eventId),
                'name']);
        },
    },
    schedule: {
        filteredSchedulePartial: function filteredSchedulePartial(args) {

            return composeBaseUrl([args.locale, 'apps/schedule/in-play']) +
                getScheduleSportFilterPartialUrl(args.sport) +
                getScheduleDateFilterPartialUrl(args.date) +
                getScheduleStreamingFilterPartialUrl(args.streaming);
        },
        eventListFragment: function eventListFragment(args) {

            return composeFragmentUrl('schedule/eventList', [args.locale]) +
                getScheduleSportFilterPartialUrl(args.sport) +
                getScheduleDateFilterPartialUrl(args.date) +
                getScheduleStreamingFilterPartialUrl(args.streaming) +
                getSchedulePageNumberPartialUrl(args.pageNumber);
        },
        sportFilterFragment: function sportFilterFragment(args) {

            return composeFragmentUrl('schedule/dateFilter', [args.locale]) +
                getScheduleDateFilterPartialUrl(args.date) +
                getScheduleStreamingFilterPartialUrl(args.streaming);
        },
        dateFilterFragment: function dateFilterFragment(args) {

            return composeFragmentUrl('schedule/sportFilter', [args.locale]) +
                getScheduleSportFilterPartialUrl(args.sport) +
                getScheduleStreamingFilterPartialUrl(args.streaming);
        },
    },
    inPlay: {
        allSportsFragment: function allSportsFragment(args) {
            return composeFragmentUrl('inPlayPage', clearUrlParameters([
                args.locale,
                'all',
                this.streamedFragment(args.streamed),
                args.pagination,
            ]));
        },
        allSportsFull: function allSportsFull(args) {
            return composeBaseUrl(clearUrlParameters([
                args.locale,
                'in-play/all',
                this.streamedFragment(args.streamed)])
            );
        },
        singleSportFragment: function singleSportFragment(args) {
            return composeFragmentUrl('inPlayPage', clearUrlParameters([
                args.locale,
                args.sport,
                this.streamedFragment(args.streamed),
                args.pagination,
            ]));
        },
        singleSportFull: function singleSportFull(args) {
            return composeBaseUrl(clearUrlParameters([
                args.locale,
                'in-play',
                args.sport,
                this.streamedFragment(args.streamed),
            ]));
        },
        base: function base(args) {

            //Needed for all in-play sports url
            if (api.allSports.indexOf(args.sport) > -1) {
                return composeBaseUrl(clearUrlParameters([
                    args.locale,
                    'in-play/all',
                    this.streamedFragment(args.streamed),
                ]));
            } else {
                return composeBaseUrl(clearUrlParameters([
                    args.locale,
                    args.sport,
                    'in-play',
                    this.streamedFragment(args.streamed),
                ]));
            }
        },
        streamedFragment: function streamedFragment(streamed){
            return streamed ? 'streamed' : '';
        },
    },
    virtual: {
        base: function base(args) {

            return composeBaseUrl([args.locale, 'apps/virtual', getVirtualSport(args.sport)]);
        },
    },
    coupons: {
        coupon: function coupon(args) {

            return composeBaseUrl([args.locale, args.sport, 'coupons', args.sortOrder, args.couponId]);
        },
        couponFragment: function couponFragment(args) {

            return composeFragmentUrl('coupons', [args.locale, args.sport, args.sortOrder,
                args.couponId]);
        },
        base: function base(args) {

            return composeBaseUrl([args.locale, args.sport, 'coupons']);
        },
    },
    promotions: {
        base: function base(args) {

            return composeBaseUrl([args.locale, 'apps/promotions']);
        },
    },
    homepage: {
        base: function base(args) {

            return composeBaseUrl([args.locale]);
        },
        homeFragment: function homeFragment(args) {

            var urlParts = [args.tabName, args.locale];

            if (args.tabName === 'inPlay') {

                urlParts.push('all');
            }

            return composeFragmentUrl('home', urlParts);
        },
        topBetsFragment: function topBetsFragment(args) {

            return composeFragmentUrl(args.tabName, [args.locale, args.sport]);
        },
    },
    topBets: {
        topBetsFragment: function topBetsFragment(args) {

            return composeFragmentUrl('topBets', [args.locale, args.sport]);
        },
    },
    login: {

        getLoginURL: function getLoginURL(params) {

            return 'https://' + params.host + '/bet/' + params.lang +
                '?action=GetSslXLoginForm&lang=' + params.lang + '&t=' + new Date().getTime() + '&LastLogin=1';
        },
    },
    streaming: {

        getStreamsURL: function getStreamsURL(params) {

            var query = '?responseFormat=IDS' + (params.sportName ? '&sport=' + params.sportName : '');

            return window.whStatic.config.streaming.streamBaseURL +
                '/api/streams/events' + query;
        },
    },
};

export default api;
